import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, Stack, Typography } from '@mui/material';
import { RadioGroupOptions } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import DrawerTitle from 'src/components/drawers/DrawerTitle';
import CompanyPhoneDetails from 'src/components/fragments/CompanyPhoneDetails';
import LoadingState from 'src/components/fragments/LoadingState';
import useCompanyPhones, { CompanyPhoneAllocation } from 'src/hooks/useCompanyPhones';
import useUserPermissions from 'src/hooks/useUserPermissions';
import { selectEnterpriseId, selectFacilityId } from 'src/store/configSlice';

const DrawerName = 'Select Device Drawer';

export const PersonalDeviceTypes = {
    ManagerPhone: 'Manager Phone',
    Computer: 'Computer',
};

export type DeviceSelection = {
    isAutoSelected?: boolean;
    isPersonalDevice?: boolean;
    isPreviouslySaved?: boolean;
    isTakeoverDevice?: boolean;
    phoneAllocation?: CompanyPhoneAllocation;
    personalDeviceType?: string;
};

export const PersonalDeviceOptions = [
    {
        label: (
            <Box>
                <Typography variant="body2" sx={{ color: 'common.black', fontWeight: 'bold' }}>
                    {PersonalDeviceTypes.ManagerPhone}
                </Typography>
            </Box>
        ),
        value: PersonalDeviceTypes.ManagerPhone,
    },
    {
        label: (
            <Box>
                <Typography variant="body2" sx={{ color: 'common.black', fontWeight: 'bold' }}>
                    {PersonalDeviceTypes.Computer}
                </Typography>
            </Box>
        ),
        value: PersonalDeviceTypes.Computer,
    },
];

function SelectDeviceDrawer(props: DrawerComponentProps) {
    const { onNext } = props;

    const { trackEvent } = useContext(AnalyticsContext);
    const enterpriseId = useSelector(selectEnterpriseId);
    const facilityId = useSelector(selectFacilityId);

    const { isInManagementGroup } = useUserPermissions();

    const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();

    const {
        availableCompanyPhones,
        unavailableCompanyPhones,
        isFetching,
        errorFetchingCompanyPhones,
        errorFetchingOnCallStaffMembers,
        hasError,
        isLoading,
        refetchCompanyPhones,
        refetchStaffMembers,
    } = useCompanyPhones();

    const handleSelectDeviceOption = (deviceId: string) => {
        trackEvent('Device Option Selected', {
            objectType: ObjectTypes.Radio,
            context: DrawerName,
            deviceId,
        });
        setSelectedDeviceId(deviceId);
    };

    const handleSelectDevice = () => {
        trackEvent('Device Selected', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            deviceId: selectedDeviceId,
        });

        const companyPhone = availableCompanyPhones.find(
            (phoneAllocation) => phoneAllocation.companyPhone.id === selectedDeviceId
        );
        const takeoverDevice = unavailableCompanyPhones.find(
            (phoneAllocation) => phoneAllocation.companyPhone.id === selectedDeviceId
        );

        if (takeoverDevice) {
            onNext({ phoneAllocation: takeoverDevice, isTakeoverDevice: true });
        } else if (companyPhone) {
            onNext({ phoneAllocation: companyPhone });
        } else {
            onNext({ isPersonalDevice: true, personalDeviceType: selectedDeviceId });
        }
    };

    const handleRefetch = () => {
        if (errorFetchingCompanyPhones) {
            refetchCompanyPhones();
        }

        if (errorFetchingOnCallStaffMembers) {
            refetchStaffMembers();
        }
    };

    useEffect(() => {
        if (errorFetchingCompanyPhones) {
            datadogRum.addError(errorFetchingCompanyPhones, {
                message: 'Error fetching company phones',
                facilityId,
                enterpriseId,
            });
        }
    }, [enterpriseId, errorFetchingCompanyPhones, facilityId]);

    useEffect(() => {
        if (errorFetchingOnCallStaffMembers) {
            datadogRum.addError(errorFetchingOnCallStaffMembers, {
                message: 'Error fetching on-call staff members',
                facilityId,
                enterpriseId,
            });
        }
    }, [enterpriseId, errorFetchingOnCallStaffMembers, facilityId]);

    return (
        <>
            <DrawerTitle title="What device is this?" sx={{ p: 2.5 }} />
            <DrawerBody sx={{ p: 2.5 }}>
                {/* Loading State */}
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <LoadingState loadingText="Loading company phones" />
                    </Box>
                )}

                {/* Error State */}
                {!isLoading && hasError && (
                    <Stack sx={{ alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <Typography variant="h5">
                            There was an error loading company phones
                        </Typography>
                        <Button
                            color="primary"
                            disableElevation
                            disabled={isFetching}
                            loading={isFetching}
                            fullWidth
                            onClick={handleRefetch}
                            variant="outlined"
                        >
                            Retry
                        </Button>
                    </Stack>
                )}

                {/* Fulfilled State */}
                {!isLoading && !hasError && (
                    <Stack sx={{ gap: 4, overflowY: 'auto' }}>
                        {availableCompanyPhones?.length > 0 && (
                            <Stack sx={{ gap: 1.5 }}>
                                <Typography variant="body2">Available Community Phones</Typography>
                                <RadioGroupOptions
                                    radioGroupProps={{ 'aria-label': 'available community phones' }}
                                    variant="outlined"
                                    options={availableCompanyPhones?.map((phoneAllocation) => {
                                        const { companyPhone } = phoneAllocation;

                                        return {
                                            value: companyPhone.id!,
                                            label: (
                                                <CompanyPhoneDetails
                                                    companyPhone={companyPhone}
                                                    containerProps={{ sx: { py: 1 } }}
                                                />
                                            ),
                                        };
                                    })}
                                    onSelectOption={handleSelectDeviceOption}
                                    selectedOption={selectedDeviceId}
                                />
                            </Stack>
                        )}

                        {(unavailableCompanyPhones?.length || 0) > 0 && (
                            <Stack sx={{ gap: 1.5 }}>
                                <Typography variant="h5" sx={{ color: 'grey.900' }}>
                                    In-Use Community Phones
                                </Typography>

                                <RadioGroupOptions
                                    radioGroupProps={{ 'aria-label': 'in-use community phones' }}
                                    variant="outlined"
                                    options={unavailableCompanyPhones?.map((phoneAllocation) => {
                                        const { companyPhone, staffMember } = phoneAllocation;

                                        return {
                                            value: companyPhone.id!,
                                            label: (
                                                <CompanyPhoneDetails
                                                    companyPhone={companyPhone}
                                                    staffMember={staffMember}
                                                    containerProps={{ sx: { py: 1 } }}
                                                    hideAvatar
                                                />
                                            ),
                                        };
                                    })}
                                    onSelectOption={handleSelectDeviceOption}
                                    selectedOption={selectedDeviceId}
                                />
                            </Stack>
                        )}

                        <Box sx={{ order: isInManagementGroup ? -1 : 1 }}>
                            {!isInManagementGroup && (
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    Other Devices
                                </Typography>
                            )}
                            <RadioGroupOptions
                                radioGroupProps={{ 'aria-label': 'other devices' }}
                                variant="outlined"
                                options={PersonalDeviceOptions}
                                onSelectOption={handleSelectDeviceOption}
                                selectedOption={selectedDeviceId}
                            />
                        </Box>
                    </Stack>
                )}
            </DrawerBody>

            <DrawerFooter sx={{ borderTop: 1, borderColor: 'grey.200', bgcolor: 'common.white' }}>
                <Button
                    fullWidth
                    disableElevation
                    color="primary"
                    variant="contained"
                    disabled={!selectedDeviceId}
                    onClick={handleSelectDevice}
                >
                    Select
                </Button>
            </DrawerFooter>
        </>
    );
}

export default SelectDeviceDrawer;
